"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, Users, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tastingDates = [{
  id: 1,
  date: "15 de junio, 2024",
  time: "19:00 - 21:00",
  status: "available",
  statusText: "Plazas Disponibles"
}, {
  id: 2,
  date: "5 de julio, 2024",
  time: "19:00 - 21:00",
  status: "few",
  statusText: "Pocas Plazas"
}, {
  id: 3,
  date: "12 de agosto, 2024",
  time: "19:00 - 21:00",
  status: "available",
  statusText: "Plazas Disponibles"
}];
export default function ProximasFechas() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: datesRef,
    isVisible: datesVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="próximas-fechas" className="bg-background py-12 md:py-9 lg:py-12">
      <div className="container max-w-6xl">
        <div ref={headerRef} className={`mb-16 text-center transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {}
          <h2 className="font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Próximas Fechas de Cata
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground/70">
            Únete a nosotros en el Centro Español de La Haya para una experiencia auténtica de degustación de aceite de oliva virgen extra
          </p>
        </div>

        <div ref={datesRef} className={`mb-16 space-y-4 transition-all duration-700 delay-150 ${datesVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {tastingDates.map((event, i) => <Card key={event.id} data-index={i} className="rounded-md border border-foreground/10 bg-accent/20 transition-all duration-300">
              <div className="flex flex-col gap-3 px-6 py-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
                  <div className="flex items-center gap-3">
                    <SafeHydrate><Calendar className="size-5 text-primary" /></SafeHydrate>
                    <span className="font-semibold text-foreground">{event.date}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="size-5 text-foreground/60" />
                    <span className="text-foreground/60">{event.time}</span>
                  </div>
                </div>
                <Badge variant={event.status === "available" ? "default" : "secondary"} className="w-fit">
                  <Users className="size-3" />
                  {event.statusText}
                </Badge>
              </div>
            </Card>)}
        </div>

        <div ref={formRef} className={`transition-all duration-700 delay-300 ${formVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Card className="rounded-md border border-foreground/10 bg-accent/20">
            <div className="px-6 py-6">
              {}
              <h3 className="mb-2 font-serif text-2xl font-semibold text-foreground">
                Inscríbete en una Cata
              </h3>
              <p className="mb-8 text-sm text-foreground/60">
                Completa el formulario para reservar tu lugar en una de nuestras próximas degustaciones
              </p>

              <FormProvider formId="tasting_signup_form_haya_tasting" sectionName="próximas-fechas" defaultValues={{
              name: "",
              email: "",
              phone: "",
              preferred_date: "",
              notes: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 100
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              preferred_date: {
                required: true
              },
              notes: {
                required: false
              }
            }} submitText="Confirmar Inscripción" successMessage="¡Gracias por inscribirte! Te contactaremos pronto con los detalles de la cata." errorMessage="No se pudo enviar tu inscripción. Por favor, inténtalo de nuevo.">
                <TastingSignupForm />
              </FormProvider>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}
function TastingSignupForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-primary/20 bg-primary/5 p-6">
        <p className="text-foreground">{successMessage}</p>
        <Button onClick={resetForm} className="btn mt-6 h-auto">
          Inscribirse en otra fecha
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-8 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        },
        maxLength: {
          value: 100,
          message: "Máximo 100 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-semibold">Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ana Martínez" {...field} className="border-0 border-b border-foreground bg-transparent px-0 py-1 focus-visible:ring-0 focus-visible:border-primary transition-colors h-auto" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-semibold">Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ana@example.com" {...field} className="border-0 border-b border-foreground bg-transparent px-0 py-1 focus-visible:ring-0 focus-visible:border-primary transition-colors h-auto" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-semibold">Número de Teléfono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+31 6 1234 5678" {...field} className="border-0 border-b border-foreground bg-transparent px-0 py-1 focus-visible:ring-0 focus-visible:border-primary transition-colors h-auto" />
              </FormControl>
              <FormDescription className="text-foreground/50">
                Opcional, para recordatorios del evento
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "Por favor, selecciona una fecha"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider font-semibold">Fecha de Cata Preferida</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="border-0 border-b border-foreground bg-transparent px-0 py-1 focus:ring-0 focus:border-primary transition-colors h-auto w-full">
                    <SelectValue placeholder="Selecciona una fecha" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-background border-foreground/10">
                  <SelectItem value="June 15, 2024">15 de junio, 2024</SelectItem>
                  <SelectItem value="July 5, 2024">5 de julio, 2024</SelectItem>
                  <SelectItem value="August 12, 2024">12 de agosto, 2024</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription className="text-foreground/50">
                Elige la fecha que prefieres asistir
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="notes" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem className="mt-8">
            <FormLabel className="text-xs uppercase tracking-wider font-semibold">Notas Adicionales</FormLabel>
            <FormControl>
              <Textarea placeholder="¿Alguna restricción alimentaria o pregunta?" className="min-h-[100px] resize-none border-0 border-b border-foreground bg-transparent px-0 py-1 focus-visible:ring-0 focus-visible:border-primary transition-colors h-auto" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="mt-8 rounded-lg border border-primary/20 bg-primary/5 p-4">
          <p className="text-sm text-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="btn mt-10 w-full sm:w-auto h-auto">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Enviando...
          </> : "Confirmar Inscripción"}
      </Button>
    </Form>;
}